import React from 'react';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthAudits } from '../../hooks/useGrowthAudits';
import { useGrowthTaskCreate } from '../../hooks/useGrowthTasks';
import type { GrowthAudit } from '../../hooks/useGrowthAudits';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const Breakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const BreakdownChip = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  background: var(--surfaceVariant);
  color: var(--onSurfaceVariant);
`;

const FindingList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const FindingItem = styled.li`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--outline);
  font-size: 0.9rem;
  &:last-child {
    border-bottom: none;
  }
`;

const Btn = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  cursor: pointer;
  margin-top: 0.25rem;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const Score = styled.span<{ $low?: boolean }>`
  font-weight: 700;
  color: ${({ $low }) => ($low ? 'var(--error)' : 'var(--onSurface)')};
  margin-right: 0.5rem;
`;

export default function GrowthAuditsPage() {
  const { data: audits = [], isLoading } = useGrowthAudits();
  const createTask = useGrowthTaskCreate();

  const byType = (type: GrowthAudit['audit_type']) =>
    audits.filter((a) => a.audit_type === type);

  const handleCreateTask = (finding: { title: string; detail: string; evidence_urls?: string[] }) => {
    createTask.mutate({
      title: finding.title,
      description: finding.detail,
      impact: 'med',
      source: 'audit',
      evidence: (finding.evidence_urls ?? []).map((url) => ({ url })),
    });
  };

  if (isLoading) return <Page><GrowthNav /><p>Loading audits…</p></Page>;

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Latest audits</SectionTitle>
      {(['visitor_readiness', 'local_discovery', 'engagement_momentum'] as const).map((auditType) => {
        const list = byType(auditType);
        const latest = list[0];
        if (!latest) return null;
        return (
          <Card key={latest.id}>
            <SectionTitle>
              <Score $low={latest.score < 50}>{latest.score}</Score>
              {latest.audit_type.replace(/_/g, ' ')}
            </SectionTitle>
            <Breakdown>
              {Object.entries(latest.breakdown || {}).map(([k, v]) => (
                <BreakdownChip key={k}>{k}: {String(v)}</BreakdownChip>
              ))}
            </Breakdown>
            <FindingList>
              {(latest.findings || []).map((f, i) => (
                <FindingItem key={i}>
                  <strong>{f.title}</strong> — {f.detail}
                  {f.evidence_urls?.length ? (
                    <span style={{ marginLeft: '0.5rem' }}>
                      {f.evidence_urls.map((u, j) => (
                        <a key={j} href={u} target="_blank" rel="noopener noreferrer" style={{ marginRight: '0.5rem' }}>
                          Evidence
                        </a>
                      ))}
                    </span>
                  ) : null}
                  <br />
                  <Btn type="button" onClick={() => handleCreateTask(f)} disabled={createTask.isPending}>
                    Create task
                  </Btn>
                </FindingItem>
              ))}
            </FindingList>
          </Card>
        );
      })}
    </Page>
  );
}
